import React, { useState } from 'react';
import { generateGrammarUnitDrill, generateGrammarTopicDrill } from '../services/aiDrillService';

const AIDrillButton = ({ 
  example, 
  unitData, 
  topicData, 
  userPreferences, 
  onDrillComplete 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [drillExamples, setDrillExamples] = useState([]);
  const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
  const [showDrills, setShowDrills] = useState(false);
  const [progress, setProgress] = useState({
    status: '',
    timeElapsed: 0,
    tokensUsed: 0,
    costIncurred: 0,
    isDiscountTime: false
  });

  const getText = (textObj) => {
    const key = userPreferences?.displayLanguage || 'traditional';
    return textObj[key] || textObj.tc || textObj.en || '';
  };

  const handleDrillClick = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setShowDrills(false);
    setDrillExamples([]);
    setCurrentDrillIndex(0);
    
    try {
      let result;
      
      if (unitData) {
        // Grammar Unit drill
        result = await generateGrammarUnitDrill(example, unitData, (progressData) => {
          setProgress(progressData);
        });
      } else if (topicData) {
        // Grammar Topic drill
        result = await generateGrammarTopicDrill(example, topicData, (progressData) => {
          setProgress(progressData);
        });
      }
      
      if (result?.drillExamples) {
        setDrillExamples(result.drillExamples);
        setShowDrills(true);
        onDrillComplete?.(result);
      }
    } catch (error) {
      console.error('Drill generation failed:', error);
      setProgress({
        status: 'Error occurred',
        timeElapsed: 0,
        tokensUsed: 0,
        costIncurred: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrillNavigation = (index) => {
    setCurrentDrillIndex(index);
  };

  const closeDrills = () => {
    setShowDrills(false);
    setDrillExamples([]);
    setCurrentDrillIndex(0);
  };

  if (showDrills && drillExamples.length > 0) {
    const currentDrill = drillExamples[currentDrillIndex];
    
    return (
      <div className="mt-4 bg-blue-900/20 rounded-lg p-4 border border-blue-500/30">
        {/* Drill Header */}
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-blue-300">
            {getText({ tc: 'AI 練習', sc: 'AI 练习', en: 'AI Drill' })} {currentDrillIndex + 1}/3
          </h4>
          <button
            onClick={closeDrills}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Current Drill Example */}
        <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
          <h5 className="text-md font-semibold mb-2 text-green-300">
            {getText({ tc: '例句', sc: '例句', en: 'Example' })} {currentDrillIndex + 1}:
          </h5>
          <p className="text-lg mb-2 text-blue-300">{currentDrill.sentence}</p>
          <p className="text-sm mb-2 text-yellow-300">{currentDrill.analysis}</p>
          <p className="text-sm text-gray-400">
            {getText(currentDrill.translation)}
          </p>
        </div>

        {/* Drill Navigation */}
        <div className="flex justify-center space-x-3 mb-4">
          {drillExamples.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDrillNavigation(index)}
              className={`w-12 h-12 rounded-full border-2 transition-all ${
                index === currentDrillIndex
                  ? 'bg-blue-500 border-blue-400 text-white'
                  : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* Progress Stats */}
        <div className="bg-gray-800/50 rounded-lg p-3 text-xs">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: '耗時', sc: '耗时', en: 'Time' })}
              </span>
              <span className="text-yellow-300 font-mono">
                {progress.timeElapsed.toFixed(1)}s
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: 'Token', sc: 'Token', en: 'Tokens' })}
              </span>
              <span className="text-green-300 font-mono">
                {progress.tokensUsed}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: '費用', sc: '费用', en: 'Cost' })}
              </span>
              <span className="text-red-300 font-mono">
                ¥{progress.costIncurred.toFixed(6)}
              </span>
            </div>
          </div>
          {progress.isDiscountTime && (
            <div className="text-center mt-2">
              <span className="text-green-400 text-xs">
                {getText({ tc: '優惠時段', sc: '优惠时段', en: 'Discount Time' })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <button
        onClick={handleDrillClick}
        disabled={isLoading}
        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
          isLoading
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{getText({ tc: '生成中...', sc: '生成中...', en: 'Generating...' })}</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <span>🤖</span>
            <span>{getText({ tc: 'AI 練習', sc: 'AI 练习', en: 'AI Drill' })}</span>
          </div>
        )}
      </button>

      {/* Progress Display */}
      {isLoading && (
        <div className="mt-3 bg-gray-800/50 rounded-lg p-3 text-xs">
          <div className="mb-2">
            <span className="text-blue-300">{progress.status}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: '耗時', sc: '耗时', en: 'Time' })}
              </span>
              <span className="text-yellow-300 font-mono">
                {progress.timeElapsed.toFixed(1)}s
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: 'Token', sc: 'Token', en: 'Tokens' })}
              </span>
              <span className="text-green-300 font-mono">
                {progress.tokensUsed}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block">
                {getText({ tc: '費用', sc: '费用', en: 'Cost' })}
              </span>
              <span className="text-red-300 font-mono">
                ¥{progress.costIncurred.toFixed(6)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDrillButton;
